import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { toDate } from '../lib/format';
import { PublicDownload, PublicProject, ShareLink } from '../types';

const COLLECTIONS = {
  SHARE_LINKS: 'shareLinks',
  PUBLIC_DOWNLOADS: 'publicDownloads',
  PUBLIC_PROJECTS: 'publicProjects',
  PROJECTS: 'projects',
};

/** 32 URL-safe characters from the browser's cryptographic RNG (192 bits). */
const generateHash = (): string => {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const toShareLink = (id: string, data: any): ShareLink => ({
  id,
  projectId: data.projectId,
  releaseId: data.releaseId || null,
  hash: data.hash,
  createdBy: data.createdBy,
  createdAt: toDate(data.createdAt) || new Date(),
  expiresAt: toDate(data.expiresAt) || null,
  isActive: data.isActive === true,
  maxDownloads: data.maxDownloads || null,
  downloadCount: data.downloadCount || 0,
});

export type ShareLinkState = 'ok' | 'notFound' | 'inactive' | 'expired' | 'limitReached';

export const shareLinkState = (link: ShareLink): ShareLinkState => {
  if (!link.isActive) return 'inactive';
  if (link.expiresAt && link.expiresAt.getTime() < Date.now()) return 'expired';
  if (link.maxDownloads && link.downloadCount >= link.maxDownloads) return 'limitReached';
  return 'ok';
};

export const shareService = {
  async createShareLink(
    projectId: string,
    createdBy: string,
    opts: { expiresAt?: Date; maxDownloads?: number } = {}
  ): Promise<ShareLink> {
    const hash = generateHash();
    await setDoc(doc(db, COLLECTIONS.SHARE_LINKS, hash), {
      projectId,
      releaseId: null,
      hash,
      createdBy,
      createdAt: serverTimestamp(),
      expiresAt: opts.expiresAt ? Timestamp.fromDate(opts.expiresAt) : null,
      isActive: true,
      maxDownloads: opts.maxDownloads || null,
      downloadCount: 0,
    });
    await this.syncPublicProject(projectId);
    return {
      id: hash,
      projectId,
      releaseId: null,
      hash,
      createdBy,
      createdAt: new Date(),
      expiresAt: opts.expiresAt || null,
      isActive: true,
      maxDownloads: opts.maxDownloads || null,
      downloadCount: 0,
    };
  },

  /** Public lookup by hash. Returns the link even if it is no longer usable, or null. */
  async getShareLinkByHash(hash: string): Promise<ShareLink | null> {
    try {
      const snap = await getDoc(doc(db, COLLECTIONS.SHARE_LINKS, hash));
      return snap.exists() ? toShareLink(snap.id, snap.data()) : null;
    } catch (error: any) {
      // Inactive links are not readable for visitors.
      if (error?.code === 'permission-denied') return null;
      throw error;
    }
  },

  async getPublicProject(projectId: string): Promise<PublicProject | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.PUBLIC_PROJECTS, projectId));
    if (!snap.exists()) return null;
    return { id: snap.id, name: snap.data().name || '', description: snap.data().description || '' };
  },

  async getProjectShareLinks(projectId: string): Promise<ShareLink[]> {
    const snapshot = await getDocs(query(collection(db, COLLECTIONS.SHARE_LINKS), where('projectId', '==', projectId)));
    return snapshot.docs
      .map((d) => toShareLink(d.id, d.data()))
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  },

  /** Writes the visitor log entry and bumps the link counter atomically. */
  async recordPublicDownload(
    link: ShareLink,
    releaseId: string,
    visitor: { name: string; email: string; phone: string },
    ipAddress?: string
  ): Promise<void> {
    const batch = writeBatch(db);
    batch.set(doc(collection(db, COLLECTIONS.PUBLIC_DOWNLOADS)), {
      shareLinkId: link.id,
      releaseId,
      projectId: link.projectId,
      visitorName: visitor.name.trim(),
      visitorEmail: visitor.email.trim(),
      visitorPhone: visitor.phone.trim(),
      downloadedAt: serverTimestamp(),
      ...(ipAddress ? { ipAddress } : {}),
    });
    batch.update(doc(db, COLLECTIONS.SHARE_LINKS, link.id), { downloadCount: increment(1) });
    await batch.commit();
  },

  async getPublicDownloads(link: ShareLink): Promise<PublicDownload[]> {
    const snapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.PUBLIC_DOWNLOADS),
        where('projectId', '==', link.projectId),
        where('shareLinkId', '==', link.id)
      )
    );
    return snapshot.docs
      .map((d) => ({ id: d.id, ...d.data(), downloadedAt: toDate(d.data().downloadedAt) || new Date() } as PublicDownload))
      .sort((a, b) => b.downloadedAt.getTime() - a.downloadedAt.getTime());
  },

  async deactivateShareLink(link: ShareLink): Promise<void> {
    await updateDoc(doc(db, COLLECTIONS.SHARE_LINKS, link.id), { isActive: false });
    await this.syncPublicProject(link.projectId);
  },

  /**
   * Keeps /publicProjects/{projectId} in step with the project: it exists only
   * while the project is active and has at least one active share link.
   */
  async syncPublicProject(projectId: string): Promise<void> {
    const [projectSnap, links] = await Promise.all([
      getDoc(doc(db, COLLECTIONS.PROJECTS, projectId)),
      this.getProjectShareLinks(projectId),
    ]);
    const project = projectSnap.exists() ? projectSnap.data() : null;
    const shouldBePublic =
      !!project && (project.status || 'active') === 'active' && links.some((l) => shareLinkState(l) === 'ok');

    const ref = doc(db, COLLECTIONS.PUBLIC_PROJECTS, projectId);
    if (shouldBePublic) {
      await setDoc(ref, {
        name: project!.name || '',
        description: project!.description || '',
        updatedAt: serverTimestamp(),
      });
    } else {
      await deleteDoc(ref);
    }
  },

  /**
   * Links created before the security update were stored under random ids and
   * looked up with a query. They are copied to hash-keyed documents so the
   * already shared URLs keep working. Returns the number of migrated links.
   */
  async migrateLegacyLinks(projectId: string): Promise<number> {
    const links = await this.getProjectShareLinks(projectId);
    let migrated = 0;
    for (const link of links) {
      if (!link.hash || link.id === link.hash) continue;
      await setDoc(doc(db, COLLECTIONS.SHARE_LINKS, link.hash), {
        projectId: link.projectId,
        releaseId: link.releaseId || null,
        hash: link.hash,
        createdBy: link.createdBy,
        createdAt: link.createdAt,
        expiresAt: link.expiresAt ? Timestamp.fromDate(link.expiresAt) : null,
        isActive: link.isActive,
        maxDownloads: link.maxDownloads || null,
        downloadCount: link.downloadCount || 0,
      });
      await deleteDoc(doc(db, COLLECTIONS.SHARE_LINKS, link.id));
      migrated++;
    }
    if (links.length > 0) await this.syncPublicProject(projectId);
    return migrated;
  },
};
